import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { useColorScheme } from '@mui/material/styles'; // Add this import
import Typography from '@mui/material/Typography';
import { Gear as GearIcon, Moon as MoonIcon, Sun as SunIcon } from '@phosphor-icons/react';

import type { Settings } from '@/types/v2/settings'; // Adjust the import path as necessary

import { setSettings as setPersistedSettings } from '@/lib/v2/settings/set-settings';
import { useSettings } from '@/hooks/v2/use-settings';
import type { ColorScheme } from '@/styles/theme-v2/theme/types';

export function ThemeSwitch(): React.JSX.Element {
  const { settings, setSettings } = useSettings();
  const { setColorScheme } = useColorScheme(); // Add this line

  const handleUpdate = async (values: Partial<Settings>): Promise<void> => {
    if (values.colorScheme) {
      setColorScheme(values.colorScheme);
    }

    const updatedSettings = { ...settings, ...values } satisfies Settings;

    setPersistedSettings(updatedSettings);
    setSettings(updatedSettings);
  };

  return (
    <Card>
      <CardHeader
        avatar={
          <Avatar>
            <GearIcon fontSize="var(--Icon-fontSize)" />
          </Avatar>
        }
        title="Theme options"
      />
      <CardContent>
        <Card variant="outlined">
          <RadioGroup
            sx={{
              gap: 0,
              '& .MuiFormControlLabel-root': {
                justifyContent: 'space-between',
                p: '8px 12px',
                '&:not(:last-of-type)': { borderBottom: '1px solid var(--mui-palette-divider)' },
              },
            }}
            value={settings.colorScheme} // Set the value to the current color scheme
          >
            {[
              { title: 'Light mode', description: 'Best for bright environments', value: 'light', icon: SunIcon },
              { title: 'Dark mode', description: 'Recommended for dark rooms', value: 'dark', icon: MoonIcon },
              // { title: 'System', description: "Adapts to your device's theme", value: 'system', icon: SunIcon },
            ].map((option) => (
              <FormControlLabel
                control={<Radio />}
                defaultValue={settings.colorScheme}
                key={option.value}
                label={
                  <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    <Avatar>
                      <option.icon fontSize="var(--Icon-fontSize)" />
                    </Avatar>
                    <div>
                      <Typography variant="inherit">{option.title}</Typography>
                      <Typography color="text.secondary" variant="caption">
                        {option.description}
                      </Typography>
                    </div>
                  </Stack>
                }
                labelPlacement="start"
                onChange={async (event) => {
                  const target = event.target as HTMLInputElement; // Cast to HTMLInputElement
                  await handleUpdate({ colorScheme: target.value as ColorScheme });
                }}
                value={option.value}
              />
            ))}
          </RadioGroup>
        </Card>
      </CardContent>
    </Card>
  );
}
